import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';
import './sass/main.scss'
import './index.css';
import { Modal, ConfigProvider } from 'antd';
// Onboarding components - using new feature structure
import { LoanResume, BackgroundContainer, BuilderContainer, LastStepsContainer } from './features/onboarding/components';
import { LoggedInBackgroundContainer } from './features/onboarding/components/LoggedInBackgroundContainer/LoggedInBackgroundContainer';
// Pages
import { WelcomePage } from './pages/WelcomePage/WelcomePage';
// Borrower components - using new feature structure
import { PreApprovalForm } from './features/borrower/components';
import { useAutoContinuePreApproval } from './features/borrower/hooks/useAutoContinuePreApproval';
// Broker components - using new feature structure
import { CreateAccountBroker } from './features/broker/components';
// Admin components - using new feature structure
import { ProcessingTeamForm } from './features/admin/components';
// Shared UI components - using new feature structure
import { PageLayout } from './shared/components/ui';
// Constants
import { ONBOARDING_STEPS, ONBOARDING_STEP_LOAN_PURPOSE_KEY, ONBOARDING_STEP_PROPERTY_KIND_KEY, getOnboardingStep, updateRentalOptions } from './utils/constants';

import { isLongTermRental, isPortfolio } from './utils/functions';
import { trackEvent, OptionSelected } from './utils/mixpanel';
import { isEqual } from 'lodash';
import { useAuth } from './features/auth/context/AuthContext';
import { useOnboarding } from './features/onboarding/context/OnboardingContext';
import { useLoan } from './features/onboarding/context/LoanContext';
import { useBorrower } from './features/borrower/context/BorrowerContext';
import { useBroker } from './features/broker/context/BrokerContext';
import { investmentPropertyAddressVar } from './shared/components/ui/atoms';
import { FormValues, UserData, BorrowerData, Ownership, LoanOption, SelectedIndexData } from './types';
import { useOwnership } from './features/onboarding/context/OwnershipContext';

const PROCESSING_TEAM_INITIAL_VALUES: FormValues = (
  ONBOARDING_STEPS.reduce((acc, step) => {
    const value = (
      (step.text === ONBOARDING_STEPS[5].text) ?
        step.options[step.options.length - 1].text
        :
        (step.options as any[]).find((option: any) => !option.isDisabled)?.text || ''

    )
    return {
      ...acc,
      [step.text]: value
    }
  }, {} as FormValues)
)

function App() {
  const [userData, setUserData] = useState<UserData | null>(null)

  const { user: authUser, isLoading: isCheckingAuth, isAdmin, isBorrower, isBroker } = useAuth();

  const {
    formValues: values,
    setValue,
    setMultipleValues,
    selectedIndex,
    setSelectedIndex,
    selectedIndexData,
    setSelectedIndexData,
    currentStep,
    bottomValue,
    setBottomValue,
    totalSteps
  } = useOnboarding();

  /* Borrower - using BorrowerContext */
  const {
    borrowerData,
    setBorrowerData,
    ownershipsFiltered
  } = useBorrower();

  /* Broker - using BrokerContext */
  const {
    isBrokerMode,
    setIsBrokerMode,
    showBrokerWithUsModal,
    setShowBrokerWithUsModal,
    showCreateAccountBrokerModal,
    setShowCreateAccountBrokerModal,
    toggleBrokerMode: toggleBrokerModeContext
  } = useBroker();

  const isRental = isLongTermRental(values)

  useEffect(() => {
    if (isPortfolio(values)) {
      investmentPropertyAddressVar("Main investment property address")
    } else {
      investmentPropertyAddressVar("Investment property address")
    }
  }, [values])

  const isProcessingTeamMode = isAdmin;
  const isBorrowerMode = isBorrower;

  // Set borrower data when auth user is available
  useEffect(() => {
    if (authUser && isBorrower) {
      setBorrowerData(authUser as BorrowerData);
      setShowWelcome(false);
    } else if (authUser) {
      setShowWelcome(false);
    }
  }, [authUser, isBorrower, setBorrowerData]);


  const { ownershipSelected, setOwnershipSelected } = useOwnership();

  const isMixedUseProperty = false
  const isResumeShown = (selectedIndex >= totalSteps)

  /* Application Flows */
  const rawState = new URLSearchParams(window.location.search).get("state")
  const [showWelcome, setShowWelcome] = useState<boolean>(!rawState)
  const [loggedInIndex, setLoggedInIndex] = useState<number>(0)
  const [loggedInSelectedIndexData, setLoggedInSelectedIndexData] = useState<SelectedIndexData>({ value: 0, origin: 'other' })

  const [isBackgroundCompleted, setIsBackgroundCompleted] = useState<boolean>(false)
  const onFinish = (): void => setIsBackgroundCompleted(true)

  const [showPreApprovalModal, setShowPreApprovalModal] = useState<boolean>(false)
  const onContinue = useCallback((userData: UserData | any): void => {
    if (userData.isNewBorrower) {
      setBorrowerData(null)
    }
    setShowPreApprovalModal(false);
    
    // In processing team mode, ensure userData has the ownership _id for CreateLoanWithOwnership
    // IMPORTANT: Preserve ALL properties from userData, including investmentAddress
    let finalUserData: any = { ...userData };
    
    if (isProcessingTeamMode && borrowerData && ownershipSelected) {
      // Add borrower but preserve all existing properties (including investmentAddress)
      finalUserData = {
        ...userData,
        borrower: (userData as any).borrower || borrowerData,
        isNewBorrower: false
      };
    }
    
    setUserData(finalUserData)
  }, [setBorrowerData, isProcessingTeamMode, borrowerData, ownershipSelected])

  const { selectedLoanOption: selectedOption, selectLoanOption } = useLoan();

  // Auto-continue if user has address and is authenticated
  const shouldAutoContinue = useAutoContinuePreApproval({
    showPreApprovalModal,
    values,
    isProcessingTeamMode,
    onContinue,
  });

  const onSelectLoanOption = (option: LoanOption): void => {
    selectLoanOption(option)
    trackEvent(OptionSelected(option.caption || "BALANCED OPTION"))
    setShowTermSheet(true)
    if (isBrokerMode) {
      setShowCreateAccountBrokerModal(true)
    } else {
      setShowPreApprovalModal(true)
    }
  }

  const [showTermSheet, setShowTermSheet] = useState<boolean>(false)

  // Edit flow uses a separate duplicated component; do not reset global onboarding values

  useEffect(() => {
    if (isProcessingTeamMode) {
      setSelectedIndex(ONBOARDING_STEPS.length)
      setShowWelcome(false)
      if (isEqual(values, {})) {
        setMultipleValues(PROCESSING_TEAM_INITIAL_VALUES)
      }
    }
  }, [selectedIndex, isProcessingTeamMode])

  useEffect(() => {
    if (isBroker && selectedIndex < ONBOARDING_STEPS.length) {
      setSelectedIndex(ONBOARDING_STEPS.length, { force: true })
      setShowWelcome(false)
    }
  }, [isBroker, selectedIndex, setSelectedIndex])

  // Auto-fill form with loan data when broker mode and ownership is selected
  const lastProcessedBrokerOwnershipIdRef = useRef<string | null>(null)
  const currentBrokerValuesRef = useRef<FormValues>(values)
  useEffect(() => {
    // Update ref with current values
    currentBrokerValuesRef.current = values

    if (!isBroker) {
      lastProcessedBrokerOwnershipIdRef.current = null
      return;
    }

    // If a "new guarantor" is selected, don't auto-overwrite it with the first ownership
    if (ownershipSelected?.isNew) {
      lastProcessedBrokerOwnershipIdRef.current = null
      return;
    }

    if (!ownershipsFiltered || ownershipsFiltered.length === 0) {
      return;
    }

    const findOwnershipById = (id: string | undefined | null): Ownership | undefined =>
      id ? ownershipsFiltered.find((ownership) => ownership._id === id) : undefined;

    const resolvedOwnership =
      findOwnershipById(ownershipSelected?._id) || ownershipsFiltered[0];

    if (!ownershipSelected || ownershipSelected._id !== resolvedOwnership._id) {
      setOwnershipSelected(resolvedOwnership);
      return;
    }

    // Only process if this is a different ownership than the last one we processed
    const currentOwnershipId = resolvedOwnership._id
    if (lastProcessedBrokerOwnershipIdRef.current === currentOwnershipId) {
      return
    }

    const loans = Array.isArray(resolvedOwnership.loans) ? resolvedOwnership.loans : [];
    const lastLoan = loans[loans.length - 1];
    const loanData = lastLoan?.loanData ?? resolvedOwnership.loanData;

    if (!loanData || typeof loanData !== 'object') {
      lastProcessedBrokerOwnershipIdRef.current = currentOwnershipId
      return;
    }

    const updates: Partial<FormValues> = {};
    const propertyKindStepText = getOnboardingStep(ONBOARDING_STEP_PROPERTY_KIND_KEY)?.text;

    ONBOARDING_STEPS.forEach((step) => {
      // Skip preselecting property kind - user should always select manually
      if (step.text === propertyKindStepText) {
        return;
      }
      const value = (loanData as Record<string, unknown>)[step.text];
      if (value !== undefined && value !== null && value !== '' && !currentBrokerValuesRef.current[step.text]) {
        updates[step.text] = value as string | number;
      }
    });

    const numericFields: Array<keyof FormValues> = [
      'price',
      'currentLoanBalance',
      'loanBalance',
      'constructionBudget',
      'afterRepairValue',
      'monthlyRent',
      'annualTaxes',
      'annualInsurance',
      'annualHoa',
    ];

    numericFields.forEach((fieldKey) => {
      const value = (loanData as Record<string, unknown>)[fieldKey as string];
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (currentBrokerValuesRef.current[fieldKey] === undefined || currentBrokerValuesRef.current[fieldKey] === null)
      ) {
        updates[fieldKey] = value as number;
      }
    });

    if (
      typeof (loanData as Record<string, unknown>).address === 'string' &&
      !currentBrokerValuesRef.current['Investment property address']
    ) {
      // Format address to remove "USA" before storing in values
      const rawAddress = (loanData as Record<string, any>).address;
      updates['Investment property address'] = rawAddress;
    }

    if (Object.keys(updates).length > 0) {
      setMultipleValues(updates);
      lastProcessedBrokerOwnershipIdRef.current = currentOwnershipId
    } else {
      lastProcessedBrokerOwnershipIdRef.current = currentOwnershipId
    }

    const selectedOptionFromLoan = (loanData as Record<string, unknown>).selectedOption;
    if (!selectedOption && selectedOptionFromLoan && typeof selectedOptionFromLoan === 'object') {
      selectLoanOption(selectedOptionFromLoan as LoanOption);
    }
  }, [
    isBroker,
    ownershipSelected?._id,
    ownershipsFiltered,
    selectLoanOption,
    selectedOption,
    setMultipleValues,
    setOwnershipSelected,
  ])

  // Auto-select propertyType for brokers when loanOwner is selected
  useEffect(() => {
    if (!isBroker) {
      return;
    }

    const loanOwnerStepText = getOnboardingStep(ONBOARDING_STEP_LOAN_PURPOSE_KEY)?.text;
    const propertyKindStepText = getOnboardingStep(ONBOARDING_STEP_PROPERTY_KIND_KEY)?.text;

    const hasLoanOwner = values[loanOwnerStepText];
    const hasPropertyType = values[propertyKindStepText];

    // Only auto-select if loanOwner is selected but propertyType is not
    if (hasLoanOwner && !hasPropertyType) {
      const propertyKindStep = getOnboardingStep(ONBOARDING_STEP_PROPERTY_KIND_KEY);
      const firstAvailableOption = propertyKindStep?.options?.find(
        (option: any) => !option.isDisabled && !option.isSpecial
      );

      if (firstAvailableOption) {
        setValue(propertyKindStepText, firstAvailableOption.text);
      }
    }
  }, [isBroker, values, setValue]);

  // Reset and auto-select ownership when borrower changes in isProcessingTeamMode
  useEffect(() => {
    if (!isProcessingTeamMode) {
      return;
    }

    if (!borrowerData) {
      setOwnershipSelected(null);
      return;
    }

    if (!ownershipsFiltered || ownershipsFiltered.length === 0) {
      setOwnershipSelected(null);
      return;
    }

    const findOwnershipById = (id: string | undefined | null): Ownership | undefined =>
      id ? ownershipsFiltered.find((ownership) => ownership._id === id) : undefined;

    const currentOwnershipId = ownershipSelected?._id;
    const resolvedOwnership =
      findOwnershipById(currentOwnershipId) || ownershipsFiltered[0];

    // Only update if the ownership has changed
    if (!currentOwnershipId || currentOwnershipId !== resolvedOwnership._id) {
      setOwnershipSelected(resolvedOwnership);
    }
  }, [
    isProcessingTeamMode,
    borrowerData?._id, // Only depend on borrower ID, not the whole object
    ownershipsFiltered,
    setOwnershipSelected,
  ]);

  // Auto-fill form with last loan data when isProcessingTeamMode and ownership is selected
  const lastProcessedOwnershipIdRef = useRef<string | null>(null)
  const currentValuesRef = useRef<FormValues>(values)
  useEffect(() => {
    // Update ref with current values
    currentValuesRef.current = values

    if (!isProcessingTeamMode) {
      return;
    }

    if (!ownershipsFiltered || ownershipsFiltered.length === 0) {
      return;
    }

    if (!ownershipSelected || ownershipSelected.isNew) {
      lastProcessedOwnershipIdRef.current = null
      return;
    }

    // Only process if this is a different ownership than the last one we processed
    const currentOwnershipId = ownershipSelected._id
    if (lastProcessedOwnershipIdRef.current === currentOwnershipId) {
      return
    }

    const loans = Array.isArray(ownershipSelected.loans) ? ownershipSelected.loans : [];
    const lastLoan = loans[loans.length - 1];
    const loanData = lastLoan?.loanData;

    if (!loanData || typeof loanData !== 'object') {
      lastProcessedOwnershipIdRef.current = currentOwnershipId
      return;
    }

    const updates: Partial<FormValues> = {};
    const stateStepText = 'Where is the property located?';

    // Autocomplete all steps except address and state (state is handled separately)
    ONBOARDING_STEPS.forEach((step) => {
      // Skip address field and state field (handled separately)
      if (
        step.text === 'Investment property address' || 
        step.text === 'Main investment property address' ||
        step.text === stateStepText
      ) {
        return;
      }

      const value = (loanData as Record<string, unknown>)[step.text];
      if (value !== undefined && value !== null && value !== '' && !currentValuesRef.current[step.text]) {
        updates[step.text] = value as string | number;
      }
    });

    // Handle "Where is the property located?" - use value from loan or default to first option (Alabama)
    const stateValueFromLoan = (loanData as Record<string, unknown>)[stateStepText];
    
    if (!currentValuesRef.current[stateStepText]) {
      if (stateValueFromLoan && typeof stateValueFromLoan === 'string' && stateValueFromLoan.trim() !== '') {
        updates[stateStepText] = stateValueFromLoan;
      } else {
        // Use first option (Alabama) as default
        const stateStep = ONBOARDING_STEPS.find(step => step.text === stateStepText);
        if (stateStep && stateStep.options && stateStep.options.length > 0) {
          const firstEnabledOption = stateStep.options.find(opt => !('isDisabled' in opt) || !opt.isDisabled);
          if (firstEnabledOption) {
            updates[stateStepText] = firstEnabledOption.text;
          }
        }
      }
    }

    // Handle numeric fields
    const numericFields: Array<keyof FormValues> = [
      'price',
      'currentLoanBalance',
      'loanBalance',
      'constructionBudget',
      'afterRepairValue',
      'monthlyRent',
      'annualTaxes',
      'annualInsurance',
      'annualHoa',
    ];

    numericFields.forEach((fieldKey) => {
      const value = (loanData as Record<string, unknown>)[fieldKey as string];
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (currentValuesRef.current[fieldKey] === undefined || currentValuesRef.current[fieldKey] === null)
      ) {
        updates[fieldKey] = value as number;
      }
    });

    // Explicitly skip address - don't autocomplete it
    // The address should remain empty or use the value from "Where is the property located?"

    if (Object.keys(updates).length > 0) {
      setMultipleValues(updates);
      lastProcessedOwnershipIdRef.current = currentOwnershipId
    } else {
      lastProcessedOwnershipIdRef.current = currentOwnershipId
    }
  }, [
    isProcessingTeamMode,
    ownershipSelected?._id,
    ownershipsFiltered,
    setMultipleValues,
  ])

  // Removed: This was removing isSpecial from all options in processing team mode
  // which prevented the special class from being applied to ficoRange elements
  // useEffect(() => {
  //   if (isProcessingTeamMode) {
  //     ONBOARDING_STEPS.forEach((step) => {
  //       if (step.options) {
  //         step.options.forEach((opt) => Object.assign(opt, { isSpecial: undefined}))
  //       }
  //     })
  //   }
  // }, [isProcessingTeamMode])

  useEffect(() => {
    if (!isProcessingTeamMode) {
      updateRentalOptions(isRental)
    }
  }, [isRental, isProcessingTeamMode])

  // Broker - toggle function that also handles showWelcome
  const toggleBrokerMode = (): void => {
    setShowWelcome(false);
    toggleBrokerModeContext();
  }

  const isOnMixedPropertyForm = selectedIndex === ONBOARDING_STEPS.length + 1

  const lastStepsContainer = (
    <LastStepsContainer
      isMixedUseProperty={isMixedUseProperty}
      values={values}
      setValue={setValue}
      setMultipleValues={setMultipleValues}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      setSelectedIndexData={setSelectedIndexData}
      setBottomValue={setBottomValue}
      onFinish={onFinish}
      isBrokerMode={isBrokerMode}
      isProcessingTeamMode={isProcessingTeamMode}
      isBorrowerMode={isBorrowerMode}
      setBorrowerData={setBorrowerData}
      borrowerData={borrowerData}
      ownerships={ownershipsFiltered}
    />
  )

  const backgroundContainer = (
    <BackgroundContainer
      currentStep={currentStep}
      selectedIndexData={selectedIndexData}
      setSelectedIndexData={setSelectedIndexData}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      values={values}
      setValue={setValue}
      setBottomValue={setBottomValue}
      isBrokerMode={isBrokerMode}
      isProcessingTeamMode={isProcessingTeamMode}
      isBorrowerMode={isBorrowerMode}
      isResumeShown={isResumeShown}
      isOnMixedPropertyForm={isOnMixedPropertyForm}
      lastStepsContainer={lastStepsContainer}
      toggleBrokerMode={toggleBrokerMode}
      isBrokerUser={isBroker}
    />
  )

  const builderContainer = (
    <BuilderContainer
      values={values}
      setValues={setMultipleValues}
      setValue={setValue}
      onSelectLoanOption={onSelectLoanOption}
      isBrokerMode={isBrokerMode}
      isProcessingTeamMode={isProcessingTeamMode}
    />
  )

  const createAccountBrokerModalMarkup = (
    <Modal
      className='create-account-broker-modal'
      open={showCreateAccountBrokerModal && !userData}
      centered={true}
      closable={true}
      closeIcon={<div></div>}
      footer={null}
      width={"624px"}
      onCancel={() => setShowCreateAccountBrokerModal(false)}
      styles={{ mask: { backgroundColor: "rgba(60, 48, 42, 0.15)", backdropFilter: "blur(7px)" } }}
      maskClosable={false}
    >
      <div>
        <CreateAccountBroker values={values} onSubmission={(userData: UserData) => { setShowCreateAccountBrokerModal(false); setUserData(userData); }} />
      </div>
    </Modal>
  )

  const preApprovalModalMarkup = (
    <Modal
      className='pre-approval'
      open={showPreApprovalModal && !userData && !shouldAutoContinue()}
      // open={true}
      centered={true}
      closable={false}
      footer={null}
      width={"638px"}
      onCancel={() => onContinue({} as UserData)}
      styles={{ mask: { backdropFilter: "blur(7px)" } }}
      maskClosable={false}
      keyboard={false}
    >
      <div>
        {
          isProcessingTeamMode ?
            <ProcessingTeamForm
              values={values}
              borrowerData={borrowerData}
              setBorrowerData={setBorrowerData}
              onContinue={onContinue}
            />
            :
            <PreApprovalForm values={values} onContinue={onContinue} borrowerData={borrowerData || undefined} ownerships={ownershipsFiltered} isBrokerMode={isBrokerMode} setValue={setValue} setSelectedIndex={setSelectedIndex} />
        }
      </div>
    </Modal>
  )

  return (
    <ConfigProvider
      getPopupContainer={() => document.querySelector('.page-content-wrapper') || document.body}
    >
      <div className={isBrokerMode ? "App broker" : "App"}>
        <PageLayout
          isProcessingTeamMode={isProcessingTeamMode}
          isBorrowerMode={isBorrowerMode}
          isBrokerMode={isBrokerMode}
          borrowerData={borrowerData || undefined}
        >
          {
            !showTermSheet ? (
              isCheckingAuth ? (
                <div></div>
              ) : (
                showWelcome && !(isProcessingTeamMode || isBorrowerMode)
                  ?
                  <WelcomePage onContinue={() => setShowWelcome(false)} onBrokerContinue={toggleBrokerMode} />
                  :
                  (
                    isBorrowerMode
                      ?
                      (
                        isBackgroundCompleted
                          ? builderContainer
                          : <LoggedInBackgroundContainer
                            currentStep={loggedInIndex}
                            selectedIndexData={loggedInSelectedIndexData}
                            setSelectedIndexData={(data) => {
                              setLoggedInSelectedIndexData(data);
                              setLoggedInIndex(Number(data.value));
                            }}
                            selectedIndex={loggedInIndex}
                            setSelectedIndex={(index, options) => {
                              setLoggedInIndex(index);
                              setLoggedInSelectedIndexData({ value: index, origin: 'other' });
                            }}
                            values={values}
                            setValue={setValue}
                            setMultipleValues={setMultipleValues}
                            setBottomValue={setBottomValue}
                            isBrokerMode={isBrokerMode}
                            isResumeShown={false}
                            ownerships={ownershipsFiltered}
                            borrowerData={borrowerData}
                            setBorrowerData={setBorrowerData}
                            isProcessingTeamMode={isProcessingTeamMode}
                            isBorrowerMode={isBorrowerMode}
                            onFinish={onFinish}
                          />
                      )
                      :
                      (isBackgroundCompleted && !(isMixedUseProperty && !values.price)
                        ? builderContainer
                        : backgroundContainer)
                  )
              )
            )
              :
              <LoanResume
                selectedOption={selectedOption}
                values={values}
                userData={userData}
                borrowerData={borrowerData}
                isBrokerMode={isBrokerMode}
                isProcessingTeamMode={isProcessingTeamMode}
                onBack={() => setShowTermSheet(false)}
              />
          }
          {
            isBrokerMode ?
              createAccountBrokerModalMarkup
              :
              preApprovalModalMarkup
          }
        </PageLayout>
      </div>
    </ConfigProvider>
  );
}

export default App;